import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBKOAcao } from '@/hooks/useBKOAcao';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { requisitarAtivacao } from '@/hooks/useAtivacaoLinhas';
import { aplicarEscopoTriagem } from '@/components/kanban/triagem/triagemPedidoUtils';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { resolverDestinoRetomada } from '@/lib/retomadaDestino';
import { depoisDoClique, manterNaTriagem } from '@/components/kanban/triagem/acaoTriagem';
import type { AcaoFluxoTriagem, TriagemFluxoConfig } from './triagensFluxoConfig';

export interface ItemTriagemFluxo {
  item_id: string;
  produto: string;
  quantidade: number;
  plano_valor: number;
  subtotal: number;
  numero_cotacao_operadora: string;
}

export interface PedidoTriagemFluxo {
  ativacaoPronta?: boolean;
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  consultor_nome: string;
  equipe_nome: string;
  entrada_coluna_em: string;
  valor_total: number;
  lead_origem_id: string | null;
  telefone: string | null;
  cliente_id: string | null;
  manter_em: string | null;
  /** Quem criou o pedido (bko_id). Nas filas `porDono` o BKO vê só os seus. */
  dono_id: string | null;
  itens: ItemTriagemFluxo[];
}

export function useTriagemFluxo(config: TriagemFluxoConfig) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { executarAcaoConsolidadaAsync } = useBKOAcao();
  const { moveCard, moverPedidoJunto, retomarPedidoJunto } = usePedidoMovement();
  const { profile } = useAuth();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const quadroTriagem = config.quadro ?? 'CONTRATO_PRIME';

  const { data: pedidos = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-fluxo', quadroTriagem, config.coluna, profile?.id],
    queryFn: async () => {
      // Campos novos da view canônica ainda não estão no snapshot Supabase.
      let q = (supabase.from('v_planilha_mae') as any)
        .select('item_id, pedido_id, pedido_mae_id, numero_pedido, cnpj, razao_social, consultor_nome, equipe_nome, produto, quantidade, plano_valor, subtotal, entrada_coluna_em, lead_origem_id, contato_real_telefone, telefone_principal, cliente_whatsapp, manter_em, cliente_id, numero_cotacao_operadora, tipo_pessoa, documento, ativacao_linhas_total, ativacao_linhas_validadas, bko_id')
        .eq(quadroTriagem === 'QUALIDADE' ? 'qualidade_status' : 'coluna_atual', config.coluna)
        .eq('quadro_atual', quadroTriagem)
        .order('entrada_coluna_em', { ascending: true })
        .order('pedido_id', { ascending: true });
      // Escopo por papel — mesma régua do Kanban (BKO vê só as equipes mapeadas)
      q = await aplicarEscopoTriagem(q, profile);
      const { data, error } = await q;

      if (error) {
        onQueryError(error);
        throw error;
      }
      onQuerySuccess();

      const porPedido = new Map<string, PedidoTriagemFluxo>();
      const maeDoPedido = new Map<string, string>();
      for (const item of (data || []) as any[]) {
        const subtotal = Number(item.subtotal) || (Number(item.plano_valor) || 0) * (Number(item.quantidade) || 1);
        const familiaId = quadroTriagem === 'QUALIDADE' ? item.pedido_mae_id || item.pedido_id : item.pedido_id;
        let pedido = porPedido.get(familiaId);
        if (!pedido) {
          pedido = {
            pedido_id: familiaId,
            ativacaoPronta: item.ativacao_linhas_total > 0 && item.ativacao_linhas_total === item.ativacao_linhas_validadas,
            numero_pedido: String(item.numero_pedido || ''),
            cnpj: item.cnpj || '',
            tipo_pessoa: item.tipo_pessoa ?? null,
            documento: item.documento || item.cnpj || '',
            razao_social: item.razao_social || '',
            consultor_nome: item.consultor_nome || '',
            equipe_nome: item.equipe_nome || '',
            entrada_coluna_em: item.entrada_coluna_em || '',
            valor_total: 0,
            lead_origem_id: item.lead_origem_id || null,
            telefone: item.contato_real_telefone || item.telefone_principal || item.cliente_whatsapp || null,
            cliente_id: item.cliente_id || null,
            manter_em: item.manter_em || null,
            dono_id: item.bko_id || null,
            itens: [],
          };
          porPedido.set(familiaId, pedido);
          maeDoPedido.set(familiaId, item.pedido_mae_id || item.pedido_id);
        }
        if (item.entrada_coluna_em && (!pedido.entrada_coluna_em || item.entrada_coluna_em < pedido.entrada_coluna_em)) {
          pedido.entrada_coluna_em = item.entrada_coluna_em;
        }
        pedido.valor_total += subtotal;
        pedido.itens.push({
          item_id: item.item_id,
          produto: item.produto || '',
          quantidade: Number(item.quantidade) || 0,
          plano_valor: Number(item.plano_valor) || 0,
          subtotal,
          numero_cotacao_operadora: String(item.numero_cotacao_operadora || ''),
        });
      }

      /* O pedido é de quem o criou, e o pedido anda inteiro: o filho quebrado
         por linha herda o BKO de qualquer irmão do mesmo pedido mãe. Sem isso,
         o filho sem bko_id aparece como "sem dono" para todos os BKO. */
      const maesSemDono = [...new Set(
        [...porPedido.values()].filter((p) => !p.dono_id).map((p) => maeDoPedido.get(p.pedido_id)!),
      )];
      if (maesSemDono.length > 0) {
        const donoDaMae = new Map<string, string>();
        for (let i = 0; i < maesSemDono.length; i += 200) {
          const lote = maesSemDono.slice(i, i + 200);
          const { data: irmaos } = await supabase
            .from('pedidos')
            .select('id, pedido_mae_id, bko_id, bko_criacao_id')
            .or(`pedido_mae_id.in.(${lote.join(',')}),id.in.(${lote.join(',')})`);
          for (const r of (irmaos || []) as { id: string; pedido_mae_id: string | null; bko_id: string | null; bko_criacao_id: string | null }[]) {
            const mae = r.pedido_mae_id || r.id;
            if (r.bko_id) donoDaMae.set(mae, r.bko_id);
            else if (r.bko_criacao_id && !donoDaMae.has(mae)) donoDaMae.set(mae, r.bko_criacao_id);
          }
        }
        for (const p of porPedido.values()) {
          if (!p.dono_id) p.dono_id = donoDaMae.get(maeDoPedido.get(p.pedido_id)!) ?? null;
        }
      }

      // Fila FIFO: não-mantidos primeiro (mais antigo na frente); "mantidos"
      // (passados para o fim com justificativa) vão para o final, na ordem do manter
      return Array.from(porPedido.values()).sort((a, b) => {
        if (!!a.manter_em !== !!b.manter_em) return a.manter_em ? 1 : -1;
        if (a.manter_em && b.manter_em) return a.manter_em.localeCompare(b.manter_em);
        return (a.entrada_coluna_em || '9999').localeCompare(b.entrada_coluna_em || '9999');
      });
    },
    refetchInterval,
  });

  const invalidateAllQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['triagem-fluxo'] });
    queryClient.invalidateQueries({ queryKey: ['triagem-pedido-fechado'] });
    queryClient.invalidateQueries({ queryKey: ['triagem-criacao-pedido'] });
    queryClient.invalidateQueries({ queryKey: ['triagem-biometria'] });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['planilha-itens'] });
    queryClient.invalidateQueries({ queryKey: ['v_planilha_mae'] });
  };

  // "Passar para o fim da fila" (padrão Manter): nota na timeline, justificativa
  // da fila (quando a fila pede) e manter_em numa ida só (`fn_triagem_manter`)
  const manterNoFim = useMutation({
    mutationFn: async ({ pedido, justificativa, prd, motivoCodigo, acaoCodigo, justificativaDaFila }: {
      pedido: PedidoTriagemFluxo; justificativa: string; prd?: string;
      motivoCodigo?: string | null; acaoCodigo?: string | null; justificativaDaFila?: boolean;
    }) => {
      await manterNaTriagem({
        pedidoId: pedido.pedido_id,
        itens: pedido.itens.map((i) => i.item_id),
        coluna: config.coluna,
        nota: `🕐 ${config.titulo} — passado para o fim da fila: ${justificativa}${prd ? ` (PRD ${prd})` : ''}`,
        justificativa: justificativaDaFila ? justificativa : null,
        motivo: motivoCodigo,
        acao: acaoCodigo,
      });
      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido };
    },
    onSuccess: ({ pedidoId, numeroPedido }) => {
      toast({
        title: 'Passado para o fim da fila',
        description: `Pedido ${numeroPedido} adiado com justificativa — o próximo da fila liberou.`,
      });
      void depoisDoClique(queryClient, ['triagem-fluxo'], { pedidoId }, 'desce');
    },
    onError: (error) => {
      toast({
        title: 'Erro ao passar para o fim da fila',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  // Executa a ação do topo: consolidada = motor do BKO (webhooks/atribuições/
  // roteamento móvel-fixa preservados); mover = pedido inteiro item a item
  const executarAcao = useMutation({
    mutationFn: async ({ pedido, acao, agenda }: { pedido: PedidoTriagemFluxo; acao: AcaoFluxoTriagem; agenda?: string }) => {
      if (quadroTriagem === 'QUALIDADE') {
        const motivo = acao.motivo || `${config.titulo} — ${acao.legenda}`;
        if (acao.destinoColuna === 'ATIVAÇÃO DE CHIP') {
          await requisitarAtivacao(`pedidos/${pedido.pedido_id}/iniciar`, {});
        } else if (config.coluna === 'ATIVAÇÃO DE CHIP' && acao.destinoColuna === 'BOAS-VINDAS') {
          await requisitarAtivacao(`pedidos/${pedido.pedido_id}/boas-vindas`, { motivo });
        } else if (acao.destinoColuna === 'PRIMEIRA FATURA') {
          await requisitarAtivacao(`pedidos/${pedido.pedido_id}/concluir-ciclo`, { etapa: 'boas_vindas', motivo });
        } else if (config.coluna === 'PRIMEIRA FATURA') {
          await requisitarAtivacao(`pedidos/${pedido.pedido_id}/concluir-ciclo`, { etapa: 'fatura_1', motivo });
        } else throw new Error('Use o painel CRM para resolver o acesso ao portal. Esta ação não altera o status comercial.');
        return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido, itens: pedido.itens.length, acao };
      }
      if (acao.exigeCotacao) {
        const semCotacao = pedido.itens.filter((i) => !i.numero_cotacao_operadora.trim());
        if (semCotacao.length > 0) {
          throw new Error(
            `Preencha a cotação (Dados Operadora) antes de devolver para ${acao.destinoColuna}. Sem cotação em ${semCotacao.length} ite${semCotacao.length !== 1 ? 'ns' : 'm'}.`
          );
        }
      }

      if (acao.tipo === 'consolidada') {
        await executarAcaoConsolidadaAsync({
          itensIds: pedido.itens.map((i) => i.item_id),
          pedidoId: pedido.pedido_id,
          colunaAtual: config.coluna,
          origemTriagem: true,
          agenda,
        });
      } else if (acao.tipo === 'retomar') {
        // Cada item volta pro EXATO lugar de onde foi cancelado (snapshot por
        // item), todas as linhas numa transação só (Douglas 10/10/2026).
        const destinos = await Promise.all(pedido.itens.map((item) => resolverDestinoRetomada(item.item_id)));
        await retomarPedidoJunto({
          pedidoId: pedido.pedido_id,
          itensIds: pedido.itens.map((i) => i.item_id),
          destinos,
          origemTriagem: true,
          agenda,
        });
      } else {
        // O pedido anda inteiro (Douglas 10/10/2026): uma chamada, todas as
        // linhas ou nenhuma.
        await moverPedidoJunto({
          pedidoId: pedido.pedido_id,
          itensIds: pedido.itens.map((i) => i.item_id),
          quadroOrigem: quadroTriagem,
          colunaOrigem: config.coluna,
          toQuadro: (acao.destinoQuadro ?? quadroTriagem) as Parameters<typeof moveCard>[0]['toQuadro'],
          toColuna: acao.destinoColuna!,
          motivo: acao.motivo || `${config.titulo} — ${acao.legenda}`,
          origemTriagem: true,
        });
      }
      return { pedidoId: pedido.pedido_id, numeroPedido: pedido.numero_pedido, itens: pedido.itens.length, acao };
    },
    onSuccess: ({ pedidoId, numeroPedido, itens, acao }) => {
      toast({
        title: acao.legenda,
        description: `Pedido ${numeroPedido} (${itens} ite${itens !== 1 ? 'ns' : 'm'}) processado.`,
      });
      void depoisDoClique(queryClient, ['triagem-fluxo'], { pedidoId }, 'sai');
    },
    onError: (error) => {
      toast({
        title: 'Erro ao processar pedido',
        description: error instanceof Error ? error.message : 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  return {
    pedidos,
    isLoading,
    refetch,
    invalidateAllQueries,
    executarAcao: executarAcao.mutateAsync,
    manterNoFim: manterNoFim.mutateAsync,
    isMantendo: manterNoFim.isPending,
  };
}
